import { getDayDiff } from "~/route4/utils";
import { TYPE_ID, SECTION_ITEM_TYPE } from "~/route4/pt/const";
import { _ } from "~/route/globals";
import { dateFormat, standardTimeFormat } from "~/util/format";
import { node } from "~/util/dom";
import createSDSIcon from "~/ui/icon";
import { ICON, ICON_TYPE } from "~/route4/icons";

export interface ITypeItemData {
	type?: string;
	title?: string;
	delay?: boolean;
	desc?: string;
}

export interface IListItemData {
	start: {
		title: string;
		datetime: string;
	};
	end: {
		title: string;
		datetime: string;
	};
	time: number;
	text?: () => string;
	exclusion: boolean;
	items?: Array<ITypeItemData>;
	past: () => boolean;
}

export interface IStop {
	coord: [number, number];
	id: number;
	name: string;
}

export enum TEXT_CONSTANT {
	EMPTY,
	BEFORE_BUT_TODAY,
	BEFORE_BUT_TODAY_WITH_WALK,
	BEFORE_RELATIVE,
	BEFORE,
	TODAY,
	TODAY_WITH_WALK,
	FUTURE_TO_24h,
	FUTURE_TO_24h_WITH_WALK,
	FUTURE,
	FUTURE_WITH_WALK,
}

export function getTextConstant(firstDate: Date, secondDate: Date, walk = false): TEXT_CONSTANT {
	const time = firstDate.getTime();
	const now = Date.now();
	let constant = TEXT_CONSTANT.EMPTY;

	if (time < now) { // minulost
		if (firstDate.getDate() === new Date().getDate()) { // do jednoho dne
			if (walk && secondDate.getTime() > now) {
				constant = TEXT_CONSTANT.BEFORE_BUT_TODAY_WITH_WALK;
			} else {
				constant = TEXT_CONSTANT.BEFORE_BUT_TODAY;
			}
		} else { // zbytek
			const diff = getDayDiff(time, true);

			if (diff > -2) {
				constant = TEXT_CONSTANT.BEFORE_RELATIVE;
			} else {
				constant = TEXT_CONSTANT.BEFORE;
			}
		}
	} else if (firstDate.getDate() === new Date().getDate()) { // dnes, takze ukazeme cas
		constant = walk ? TEXT_CONSTANT.TODAY_WITH_WALK : TEXT_CONSTANT.TODAY;
	} else {
		const diff = getDayDiff(time);

		if (diff < 2) {
			constant = walk ? TEXT_CONSTANT.FUTURE_TO_24h_WITH_WALK : TEXT_CONSTANT.FUTURE_TO_24h;
		} else {
			constant = walk ? TEXT_CONSTANT.FUTURE_WITH_WALK : TEXT_CONSTANT.FUTURE;
		}
	}

	return constant;
}

function parseDateToText(firstDate: Date, secondDate: Date, walk = false): string {
	const time = firstDate.getTime();
	let text = "";
	const constant = getTextConstant(firstDate, secondDate, walk);

	switch (constant) {
		case TEXT_CONSTANT.BEFORE_BUT_TODAY_WITH_WALK:
			text = _("route4.pubt.cantKeep");
			break;

		case TEXT_CONSTANT.BEFORE_BUT_TODAY:
			text = _("route4.pubt.department", {
				text: "",
			});
			break;

		case TEXT_CONSTANT.BEFORE_RELATIVE:
			text = _("route4.pubt.department", {
				text: ` ${new Intl.RelativeTimeFormat(Userweb.defaultLang || "cs", { numeric: "auto" }).format(getDayDiff(time, true), "day")}`,
			});
			break;

		case TEXT_CONSTANT.BEFORE:
			text = _("route4.pubt.department", {
				text: ` ${new Intl.DateTimeFormat(Userweb.defaultLang || "cs", {
					day: "numeric",
					month: "long",
				}).format(firstDate)}`,
			});
			break;

		case TEXT_CONSTANT.TODAY_WITH_WALK:
			text = _("route4.pubt.leaveAt", {
				time: dateFormat(firstDate, "hh:MM"),
			});
			break;

		case TEXT_CONSTANT.FUTURE_TO_24h:
		case TEXT_CONSTANT.FUTURE_TO_24h_WITH_WALK:
			text = new Intl.RelativeTimeFormat(Userweb.defaultLang || "cs", { numeric: "auto" }).format(getDayDiff(time), "day");
			break;

		case TEXT_CONSTANT.FUTURE:
		case TEXT_CONSTANT.FUTURE_WITH_WALK:
			text = new Intl.DateTimeFormat(Userweb.defaultLang || "cs", {
				day: "numeric",
				month: "long",
			}).format(firstDate);
			break;

		default:
			text = "";
			break;
	}

	return text;
}

function getItemsTypeList(items, midpointindex: Array<number> = []) {
	const list = [];
	let exclusion = false;

	items.forEach((item, index) => {
		if (item.type === SECTION_ITEM_TYPE.drive) {
			list.push({
				type: TYPE_ID[item.vehicleType],
				title: item.routeName,
				delay: !!item.info.find(code => code.id === 302 || code.id === 500),
				desc: item.partDescription,
				stopId: item.startStopID,
			});

			if (!exclusion) {
				exclusion = !!item.info.find(code => code.id > 199 && code.id < 300);
			}
		} else if (midpointindex.indexOf(index) > -1) {
			list.push({
				type: "midpoint",
				title: (midpointindex.indexOf(index) + 2).toString(),
			});
		}
	});

	return {
		list,
		exclusion,
	};
}

export function preparePUBTListItemData(itemData: any, startTitle = "", endTitle = "", stops?: Array<any>): IListItemData {
	const firstDrive = itemData.items.find(item => SECTION_ITEM_TYPE.drive === item.type);
	const past = () => firstDrive ? SECTION_ITEM_TYPE.walk === itemData.items[0].type ? new Date(firstDrive.departureISO).getTime() < Date.now() : new Date(itemData.departureISO).getTime() < Date.now() : false;
	const items = getItemsTypeList(itemData.items, itemData.midpointindex);
	const text = () => firstDrive ? parseDateToText(new Date(itemData.items[0].departureISO), new Date(itemData.items[1] ? itemData.items[1].departureISO : itemData.items[0].arrivalISO), SECTION_ITEM_TYPE.walk === itemData.items[0].type) : "";

	// poresit, kdyz nebude trasa drive, ale jen pesky

	const ItemListData: IListItemData = {
		start: {
			title: "startStopIndex" in itemData && stops && stops[itemData.startStopIndex]?.name ? stops[itemData.startStopIndex]?.name : startTitle,
			datetime: dateFormat(new Date(firstDrive ? firstDrive.departureISO : itemData.departureISO), "hh:MM"),
		},
		end: {
			title: "endStopIndex" in itemData && stops && stops[itemData.endStopIndex]?.name ? stops[itemData.endStopIndex]?.name : endTitle,
			datetime: dateFormat(new Date(itemData.arrivalISO), "hh:MM"),
		},
		time: itemData.time,
		text,
		exclusion: items.exclusion,
		items: items.list,
		past,
	};

	return ItemListData;
}

export function createTotalTimeNode(time: number, exclusion: boolean) {
	const container = node("div", { className: "total-time" }, standardTimeFormat(time).string);

	if (exclusion) {
		const warning = createSDSIcon({
			symbol: ICON.warning,
		});

		warning.classList.add("warning");

		container.append(warning);
	}

	return container;
}

export function createPubtTypeItem(item: ITypeItemData, noIcon = false) {
	const container = node("mapy-pubt-type-item", {
		...item.type ? { className: item.type === TYPE_ID[4] ? `pubt-type-${item.type} pubt-type-${item.type}-${item.title.toLowerCase()}` : `pubt-type-${item.type}` } : {},
	});

	if (!noIcon && item.type && item.type !== "midpoint") {
		container.append(createSDSIcon({
			symbol: ICON_TYPE[item.type],
		}));
	}

	return container;
}

export function createTypeItem(item: ITypeItemData) {
	const container = createPubtTypeItem(item);

	if (item.title) {
		container.append(node("span", {}, item.title));
	}

	if (item.delay) {
		container.delay = true;
	}

	return container;
}
